import { db, type WorkoutRow } from './schema'

export interface SportSummary {
  sport: string
  count: number
  totalDurationMs: number
  totalStrain: number
  totalDistanceM: number
  totalKj: number
  lastDay: string | null
  isCardio: boolean
}

export interface WorkoutPoint {
  day: string
  start: string
  durationMin: number
  strain: number | null
  distanceKm: number | null
  paceMinPerKm: number | null
  avgHr: number | null
  maxHr: number | null
  kj: number | null
}

export function workoutDurationMs(w: WorkoutRow): number {
  if (!w.start || !w.end) return 0
  return +new Date(w.end) - +new Date(w.start)
}

export function isCardioSport(name: string): boolean {
  const cardio = new Set([
    'running',
    'cycling',
    'spinning',
    'mountain biking',
    'rowing',
    'swimming',
    'walking',
    'hiking',
    'elliptical',
    'stair climber',
    'kayaking',
    'paddleboarding',
    'skiing',
    'snowboarding',
    'surfing',
    'triathlon',
  ])
  return cardio.has(name.toLowerCase())
}

// Friendly display names for specific Whoop sport_name values that come back
// awkwardly. Keys are lowercase + whitespace-normalized.
const SPORT_OVERRIDES: Record<string, string> = {
  'weightlifting msk': 'Strength Training',
  'weight lifting msk': 'Strength Training',
  'weightlifting': 'Strength Training',
  'weight lifting': 'Strength Training',
  'hiit': 'HIT',
  'h.i.i.t': 'HIT',
}

export function prettySportName(raw: string | undefined): string {
  if (!raw) return 'Activity'
  const key = raw.trim().toLowerCase().replace(/[_-]+/g, ' ').replace(/\s+/g, ' ')
  if (SPORT_OVERRIDES[key]) return SPORT_OVERRIDES[key]
  return raw
    .split(/[\s_-]+/)
    .map((p) => p[0]?.toUpperCase() + p.slice(1))
    .join(' ')
}

/** Aggregate every workout into per-sport summaries. */
export async function sportSummaries(): Promise<SportSummary[]> {
  const all = await db.workouts.toArray()
  const map = new Map<string, SportSummary>()
  for (const w of all) {
    const sport = prettySportName(w.sport_name)
    let s = map.get(sport)
    if (!s) {
      s = {
        sport,
        count: 0,
        totalDurationMs: 0,
        totalStrain: 0,
        totalDistanceM: 0,
        totalKj: 0,
        lastDay: null,
        isCardio: isCardioSport(w.sport_name ?? ''),
      }
      map.set(sport, s)
    }
    s.count++
    s.totalDurationMs += workoutDurationMs(w)
    if (w.score?.strain != null) s.totalStrain += w.score.strain
    if (w.score?.distance_meter != null) s.totalDistanceM += w.score.distance_meter
    if (w.score?.kilojoule != null) s.totalKj += w.score.kilojoule
    if (!s.lastDay || w.day > s.lastDay) s.lastDay = w.day
  }
  return [...map.values()].sort((a, b) => b.count - a.count)
}

/** All workouts of a given sport, mapped to chartable points. */
export async function workoutPointsForSport(sport: string): Promise<WorkoutPoint[]> {
  const all = await db.workouts.toArray()
  const matching = all
    .filter((w) => prettySportName(w.sport_name) === sport)
    .sort((a, b) => (a.start < b.start ? -1 : 1))

  return matching.map((w) => {
    const durMs = workoutDurationMs(w)
    const distM = w.score?.distance_meter ?? null
    const distKm = distM != null ? distM / 1000 : null
    const pace =
      distKm != null && distKm > 0.1 && durMs > 0
        ? durMs / 60_000 / distKm
        : null
    return {
      day: w.day,
      start: w.start,
      durationMin: Math.round(durMs / 60_000),
      strain: w.score?.strain ?? null,
      distanceKm: distKm,
      paceMinPerKm: pace,
      avgHr: w.score?.average_heart_rate ?? null,
      maxHr: w.score?.max_heart_rate ?? null,
      kj: w.score?.kilojoule ?? null,
    }
  })
}

export interface PersonalBests {
  hardest: { value: number; day: string } | null
  longest: { value: number; day: string } | null
  furthest: { value: number; day: string } | null
  fastestPace: { value: number; day: string } | null
  highestAvgHr: { value: number; day: string } | null
}

export function personalBests(points: WorkoutPoint[]): PersonalBests {
  const byMax = (key: keyof WorkoutPoint) =>
    points.reduce<{ value: number; day: string } | null>((best, p) => {
      const v = p[key]
      if (typeof v !== 'number' || !Number.isFinite(v)) return best
      if (!best || v > best.value) return { value: v, day: p.day }
      return best
    }, null)

  const byMin = (key: keyof WorkoutPoint) =>
    points.reduce<{ value: number; day: string } | null>((best, p) => {
      const v = p[key]
      if (typeof v !== 'number' || !Number.isFinite(v) || v <= 0) return best
      if (!best || v < best.value) return { value: v, day: p.day }
      return best
    }, null)

  return {
    hardest: byMax('strain'),
    longest: byMax('durationMin'),
    furthest: byMax('distanceKm'),
    fastestPace: byMin('paceMinPerKm'),
    highestAvgHr: byMax('avgHr'),
  }
}
